import * as React from "react";
import { X } from "lucide-react";

import { ApiError, getAdmissionPolicy, saveAdmissionPolicy } from "@/api/client";
import {
  AdmissionPolicyView,
  AdmissionPredicateTerm,
  AdmissionVocabulary,
} from "@/api/types";

/**
 * Who may take this time, written as sentences.
 *
 * <p>A rule reads "If they have a verified email and their email domain is northgate.co and it is
 * about recruiting, then book it, up to 45 minutes." Every choosable part is a control and every
 * fixed part is prose, so the thing the host is authoring and the thing they are reading are the
 * same object. The alternative — a grid of predicate rows with a type column and a value column —
 * is a rule engine's data model shown to somebody who does not have one (D-67).</p>
 *
 * <p>The vocabulary comes from the server. Nothing in this file decides which clauses exist, which
 * providers can be named, or which outcomes take a duration; a second copy of that could drift,
 * and a drifted copy offers the host a sentence the server will refuse.</p>
 *
 * <p>A refusal is put back into the sentence it came from rather than shown as a form error. The
 * server answers 422 with the reason in the same words the rule is written in ("an auto-book rule
 * must rest on something verified"), prefixed with which rule, and that prefix is what lets the
 * message land under the rule that caused it.</p>
 */
export function AdmissionRulesEditor({ patternId }: { patternId: string }) {
  const [policy, setPolicy] = React.useState<AdmissionPolicyView | null>(null);
  const [rules, setRules] = React.useState<DraftRule[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [refusal, setRefusal] = React.useState<Refusal | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  const seed = React.useCallback((loaded: AdmissionPolicyView) => {
    setPolicy(loaded);
    setRules(
      loaded.rules.map((r) => ({
        outcome: r.outcome,
        maxMinutes: r.max_minutes,
        predicates: r.predicates.map((p) => ({ type: p.type, value: p.value ?? "" })),
      })),
    );
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    getAdmissionPolicy(patternId)
      .then((loaded) => {
        if (!cancelled) seed(loaded);
      })
      .catch((e) => {
        if (!cancelled) setError(describe(e));
      });
    return () => {
      cancelled = true;
    };
  }, [patternId, seed]);

  function edit(index: number, next: DraftRule) {
    setRules(rules.map((r, i) => (i === index ? next : r)));
    setRefusal(null);
    setSaved(false);
  }

  async function save() {
    if (!policy) return;
    setSaving(true);
    setError(null);
    setRefusal(null);
    try {
      seed(
        await saveAdmissionPolicy(patternId, {
          rules: rules.map((r) => ({
            outcome: r.outcome,
            max_minutes: r.maxMinutes,
            predicates: r.predicates.map((p) => ({ type: p.type, value: p.value || null })),
          })),
        }),
      );
      setSaved(true);
    } catch (e) {
      const parsed = parseRefusal(e);
      if (parsed) setRefusal(parsed);
      else setError(describe(e));
    } finally {
      setSaving(false);
    }
  }

  if (error && !policy) {
    return <p className="text-xs text-destructive-strong">{error}</p>;
  }
  if (!policy) {
    return <p className="text-xs text-muted-foreground">Loading…</p>;
  }

  const defaultOutcome = policy.vocabulary.outcomes[0]?.outcome ?? "HITL_ONLY";

  return (
    <div className="mt-3 border-t border-dashed border-border pt-3">
      <p className="font-comic-headline text-xs uppercase tracking-wide">Who may take this time</p>


      <ol className="mt-3 space-y-2">
        {rules.map((rule, i) => (
          <li key={i}>
            <RuleSentence
              index={i}
              rule={rule}
              vocabulary={policy.vocabulary}
              refusal={refusal?.ruleIndex === i ? refusal.message : null}
              onChange={(next) => edit(i, next)}
              onRemove={() => {
                setRules(rules.filter((_, j) => j !== i));
                setRefusal(null);
                setSaved(false);
              }}
            />
          </li>
        ))}
      </ol>

      {refusal && refusal.ruleIndex == null && (
        <p role="alert" className="mt-2 text-xs text-destructive-strong">
          {refusal.message}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 text-xs text-destructive-strong">
          {error}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => {
            setRules([...rules, { outcome: defaultOutcome, maxMinutes: null, predicates: [] }]);
            setSaved(false);
          }}
          className="rounded border border-border bg-background px-2 py-1 text-[11px] font-comic-headline uppercase tracking-wide hover:bg-muted"
        >
          + Add rule
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="rounded border-2 border-border bg-brand-blue px-3 py-1 text-[11px] font-comic-headline uppercase tracking-wide text-brand-blue-foreground disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save rules"}
        </button>
        {saved && <span className="text-[11px] text-muted-foreground">Saved</span>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ one sentence

interface DraftPredicate {
  type: string;
  value: string;
}

interface DraftRule {
  outcome: string;
  maxMinutes: number | null;
  predicates: DraftPredicate[];
}

export function RuleSentence({
  index,
  rule,
  vocabulary,
  refusal,
  onChange,
  onRemove,
}: {
  index: number;
  rule: DraftRule;
  vocabulary: AdmissionVocabulary;
  refusal: string | null;
  onChange: (next: DraftRule) => void;
  onRemove: () => void;
}) {
  const outcome = vocabulary.outcomes.find((o) => o.outcome === rule.outcome);
  const unused = vocabulary.predicates.filter(
    (t) => !rule.predicates.some((p) => p.type === t.type),
  );

  function setPredicate(i: number, next: DraftPredicate) {
    onChange({ ...rule, predicates: rule.predicates.map((p, j) => (j === i ? next : p)) });
  }

  return (
    <div
      className={`rounded-md border bg-background p-2 text-xs ${
        refusal ? "border-destructive" : "border-border"
      }`}
    >
      <div className="flex items-start gap-2">
        <span className="mt-1 font-comic-headline text-[11px] text-muted-foreground">
          {index + 1}.
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {rule.predicates.length === 0 ? (
            // A rule with no conditions applies to everybody, which is what a trailing catch-all
            // is for. Saying "Anyone else" rather than "If" is the same rule, read honestly.
            <span className="font-medium">Anyone else</span>
          ) : (
            rule.predicates.map((predicate, i) => (
              <PredicateClause
                key={i}
                lead={i === 0 ? "If" : "and"}
                predicate={predicate}
                vocabulary={vocabulary}
                  taken={rule.predicates.filter((_, j) => j !== i).map((p) => p.type)}
                onChange={(next) => setPredicate(i, next)}
                onRemove={() =>
                  onChange({
                    ...rule,
                    predicates: rule.predicates.filter((_, j) => j !== i),
                  })
                }
              />
            ))
          )}

          <div className="flex flex-wrap items-center gap-1">
            <span className="text-muted-foreground">then</span>
            <select
              aria-label={`Rule ${index + 1} outcome`}
              value={rule.outcome}
              onChange={(e) => {
                const next = vocabulary.outcomes.find((o) => o.outcome === e.target.value);
                onChange({
                  ...rule,
                  outcome: e.target.value,
                  // A limit only means something on an outcome that offers time; the server
                  // refuses one that does not, so drop it rather than carry it into a refusal.
                  maxMinutes: next?.caps_duration ? rule.maxMinutes : null,
                });
              }}
              className="rounded border border-border bg-background px-1 py-0.5"
            >
              {vocabulary.outcomes.map((o) => (
                <option key={o.outcome} value={o.outcome}>
                  {o.clause}
                </option>
              ))}
            </select>
            {outcome?.caps_duration && (
              <>
                <span className="text-muted-foreground">up to</span>
                <input
                  type="number"
                  min={1}
                  aria-label={`Rule ${index + 1} limit in minutes`}
                  value={rule.maxMinutes ?? ""}
                  placeholder="any"
                  onChange={(e) =>
                    onChange({
                      ...rule,
                      maxMinutes: e.target.value === "" ? null : Number(e.target.value),
                    })
                  }
                  className="w-16 rounded border border-border bg-background px-1 py-0.5"
                />
                <span className="text-muted-foreground">minutes</span>
              </>
            )}
          </div>

          {unused.length > 0 && (
            <div>
              <select
                aria-label={`Add a condition to rule ${index + 1}`}
                value=""
                onChange={(e) => {
                  if (!e.target.value) return;
                  onChange({
                    ...rule,
                    predicates: [...rule.predicates, { type: e.target.value, value: "" }],
                  });
                }}
                className="rounded border border-dashed border-border bg-background px-1 py-0.5 text-[11px] text-muted-foreground"
              >
                <option value="">+ and…</option>
                {unused.map((t) => (
                  <option key={t.type} value={t.type}>
                    {t.clause}
                  </option>
                ))}
              </select>
            </div>
          )}

          {refusal && (
            <p role="alert" className="text-[11px] text-destructive-strong">
              {refusal}
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove rule ${index + 1}`}
          className="mt-0.5 shrink-0 rounded p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive-strong"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function PredicateClause({
  lead,
  predicate,
  vocabulary,
  taken,
  onChange,
  onRemove,
}: {
  lead: string;
  predicate: DraftPredicate;
  vocabulary: AdmissionVocabulary;
  taken: string[];
  onChange: (next: DraftPredicate) => void;
  onRemove: () => void;
}) {
  const term = vocabulary.predicates.find((t) => t.type === predicate.type);
  const selectable = vocabulary.predicates.filter(
    (t) => t.type === predicate.type || !taken.includes(t.type),
  );

  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="text-muted-foreground">{lead}</span>
      <select
        aria-label="Condition"
        value={predicate.type}
        onChange={(e) => onChange({ type: e.target.value, value: "" })}
        className="rounded border border-border bg-background px-1 py-0.5"
      >
        {selectable.map((t) => (
          <option key={t.type} value={t.type}>
            {t.clause}
          </option>
        ))}
      </select>
      {term && <ClauseValue term={term} predicate={predicate} onChange={onChange} />}
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove condition"
        className="rounded p-0.5 text-muted-foreground hover:text-destructive-strong"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

/** The blank after a clause, filled the way the served vocabulary says it is filled. */
function ClauseValue({
  term,
  predicate,
  onChange,
}: {
  term: AdmissionPredicateTerm;
  predicate: DraftPredicate;
  onChange: (next: DraftPredicate) => void;
}) {
  if (term.value_source === "NONE") return null;

  if (term.value_source === "FREE_TEXT") {
    return (
      <input
        type="text"
        aria-label={term.clause}
        value={predicate.value}
        placeholder={term.example ?? ""}
        onChange={(e) => onChange({ ...predicate, value: e.target.value })}
        className="min-w-32 flex-1 rounded border border-border bg-background px-1 py-0.5"
      />
    );
  }

  // A closed set on both ends: providers come from the vocabulary, purposes from what the host
  // wrote above. Neither can be typed, so neither can be saved and then never match.
  const options = term.choices;
  const chosen = splitValues(predicate.value);

  if (options.length === 0) {
    return <span className="text-[11px] text-muted-foreground">(name a purpose above first)</span>;
  }

  return (
    <span className="flex flex-wrap items-center gap-1">
      {options.map((option) => {
        const on = chosen.includes(option.value.toLowerCase());
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={on}
            onClick={() =>
              onChange({
                ...predicate,
                value: toggle(chosen, option.value, term.multiple).join(", "),
              })
            }
            className={`rounded-full border px-2 py-0.5 text-[11px] ${
              on ? "border-border bg-brand-blue text-brand-blue-foreground" : "border-border bg-background"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </span>
  );
}


// ------------------------------------------------------------------ plumbing

interface Refusal {
  /** Which rule it belongs under, or null when it is about the document as a whole. */
  ruleIndex: number | null;
  message: string;
}

/**
 * Pull the rule number out of a 422 so the reason can be shown under the rule that caused it.
 *
 * <p>The server prefixes "rule N: " precisely so this is possible; the number is 1-based there
 * because it is written for a person, and 0-based here because it indexes an array.</p>
 */
export function parseRefusal(e: unknown): Refusal | null {
  if (!(e instanceof ApiError) || e.status !== 422) return null;
  let detail = "";
  try {
    detail = JSON.parse(e.body || "{}").detail ?? "";
  } catch {
    return null;
  }
  if (!detail) return null;
  const match = /^rule (\d+): (.*)$/s.exec(detail);
  if (!match) return { ruleIndex: null, message: detail };
  return { ruleIndex: Number(match[1]) - 1, message: match[2] };
}

export function splitValues(value: string): string[] {
  return value
    .split(",")
    .map((v) => v.trim().toLowerCase())
    .filter((v) => v.length > 0);
}

export function toggle(chosen: string[], candidate: string, multiple: boolean): string[] {
  const value = candidate.toLowerCase();
  if (chosen.includes(value)) return chosen.filter((v) => v !== value);
  return multiple ? [...chosen, value] : [value];
}

function describe(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 402) return "Admission rules are part of Pro. Upgrade to write them.";
    try {
      const detail = JSON.parse(e.body || "{}").detail;
      if (typeof detail === "string") return detail;
    } catch {
      /* fall through */
    }
  }
  return "Something went wrong. Please try again.";
}
